import React, { useState, useEffect, useRef } from 'react';
import { Card, Column, Project, Client, User, SubTask, Comment, StageLog } from '../types';
import {
  updateCard,
  deleteCard,
  createSubTask,
  updateSubTask,
  deleteSubTask,
  createComment,
  getUsers,
} from '../api/client';
import { format } from 'date-fns';

interface Props {
  card: Card;
  columns: Column[];
  projects: Project[];
  clients: Client[];
  users: User[];
  onClose: () => void;
  onUpdate: (card: Card) => void;
  onDelete: (cardId: string) => void;
  currentUserName: string;
  isAdmin?: boolean;
}

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'] as const;
const PRIORITY_COLORS: Record<string, string> = {
  low: '#64748B', medium: '#92400E', high: '#9A3412', urgent: '#DC2626',
};
const PRIORITY_BG: Record<string, string> = {
  low: '#F1F5F9', medium: '#FEF3C7', high: '#FED7AA', urgent: '#FEF2F2',
};

export default function CardModal({
  card: initialCard,
  columns,
  projects,
  clients,
  users,
  onClose,
  onUpdate,
  onDelete,
  currentUserName,
  isAdmin = true,
}: Props) {
  const [card, setCard] = useState<Card>(initialCard);
  const [saving, setSaving] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'activity'>('details');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter projects by client if card has a project
  const [selectedClientId, setSelectedClientId] = useState<string>(
    card.project?.clientId || ''
  );
  const filteredProjects = projects.filter(
    (p) => !selectedClientId || p.clientId === selectedClientId
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleFieldChange = async (field: string, value: string | null) => {
    if (!isAdmin) return; // read-only for clients
    const updated = { ...card, [field]: value };
    setCard(updated as Card);
    setSaving(true);
    try {
      const result = await updateCard(card.id, { [field]: value || undefined });
      setCard(result);
      onUpdate(result);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSubtask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!newSubtask.trim()) return;
    try {
      const sub = await createSubTask(card.id, { label: newSubtask.trim() });
      setCard((prev) => ({ ...prev, subTasks: [...(prev.subTasks || []), sub] }));
      setNewSubtask('');
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleSubtask = async (sub: SubTask) => {
    if (!isAdmin) return;
    try {
      const updated = await updateSubTask(card.id, sub.id, { done: !sub.done });
      setCard((prev) => ({
        ...prev,
        subTasks: prev.subTasks?.map((s) => (s.id === sub.id ? updated : s)),
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteSubtask = async (subId: string) => {
    if (!isAdmin) return;
    try {
      await deleteSubTask(card.id, subId);
      setCard((prev) => ({
        ...prev,
        subTasks: prev.subTasks?.filter((s) => s.id !== subId),
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const comment = await createComment(card.id, {
        body: newComment.trim(),
        author: currentUserName,
      });
      setCard((prev) => ({ ...prev, comments: [...(prev.comments || []), comment] }));
      setNewComment('');
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    try {
      await deleteCard(card.id);
      onDelete(card.id);
    } catch (e) {
      console.error(e);
    }
  };

  const completedSubs = card.subTasks?.filter((s) => s.done).length || 0;
  const totalSubs = card.subTasks?.length || 0;
  const progressPct = totalSubs ? Math.round((completedSubs / totalSubs) * 100) : 0;

  return (
    <div
      className="modal-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="modal fade-in" style={{ maxWidth: 720 }}>
        {/* Modal Header */}
        <div
          style={{
            padding: '24px 28px 20px',
            borderBottom: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <textarea
              value={card.title}
              onChange={(e) => setCard((prev) => ({ ...prev, title: e.target.value }))}
              onBlur={(e) => handleFieldChange('title', e.target.value)}
              readOnly={!isAdmin}
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#0F172A',
                border: 'none',
                background: 'transparent',
                width: '100%',
                resize: 'none',
                boxShadow: 'none',
                padding: '4px 8px',
                borderRadius: 8,
                lineHeight: 1.4,
                minHeight: 'auto',
              }}
              onFocus={(e) => { e.target.style.background = '#F8FAFC'; e.target.style.border = '1px solid #E2E8F0'; }}
              onBlurCapture={(e) => { e.target.style.background = 'transparent'; e.target.style.border = 'none'; }}
              rows={2}
            />
            {saving && (
              <span style={{ fontSize: 11, color: '#94A3B8', marginLeft: 8 }}>Saving…</span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: 'none',
              background: '#F1F5F9',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748B',
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #F1F5F9', padding: '0 28px' }}>
          {(['details', 'activity'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 0',
                marginRight: 24,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: activeTab === tab ? '#2563EB' : '#94A3B8',
                borderBottom: activeTab === tab ? '2px solid #2563EB' : '2px solid transparent',
                transition: 'all 0.15s',
                textTransform: 'capitalize',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '16px' : '24px 28px', display: 'flex', gap: 24, maxHeight: isMobile ? '75vh' : '60vh', overflowY: 'auto' }}>
          {activeTab === 'details' ? (
            isMobile ? (
              // ── Mobile: single column, description → subtasks → fields ──
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Description */}
                <div>
                  <label>Description</label>
                  <textarea
                    value={card.description || ''}
                    onChange={(e) => setCard((prev) => ({ ...prev, description: e.target.value }))}
                    onBlur={(e) => handleFieldChange('description', e.target.value || null)}
                    placeholder={isAdmin ? 'Add a description...' : 'No description'}
                    rows={3}
                    readOnly={!isAdmin}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {/* Subtasks */}
                <div>
                  <label>Sub-tasks {totalSubs > 0 && `(${completedSubs}/${totalSubs})`}</label>
                  {totalSubs > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progressPct}%`, background: progressPct === 100 ? '#16A34A' : '#2563EB', borderRadius: 2 }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3 }}>{progressPct}% complete</div>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {card.subTasks?.map((sub) => (
                      <div key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: sub.done ? '#F0FDF4' : '#F8FAFC', borderRadius: 8, border: `1px solid ${sub.done ? '#BBF7D0' : '#E2E8F0'}` }}>
                        <input type="checkbox" checked={sub.done} onChange={() => handleToggleSubtask(sub)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16A34A' }} />
                        <span style={{ flex: 1, fontSize: 13, color: sub.done ? '#64748B' : '#1E293B', textDecoration: sub.done ? 'line-through' : 'none' }}>{sub.label}</span>
                        {isAdmin && (
                          <button onClick={() => handleDeleteSubtask(sub.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 16, padding: '0 2px', lineHeight: 1 }}>×</button>
                        )}
                      </div>
                    ))}
                  </div>
                  {isAdmin && (
                  <form onSubmit={handleAddSubtask} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)} placeholder="Add a sub-task..." style={{ flex: 1 }} />
                    <button type="submit" className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>Add</button>
                  </form>
                  )}
                </div>

                {/* Priority */}
                <div>
                  <label>Priority</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {PRIORITY_OPTIONS.map((p) => (
                      <button key={p} onClick={() => handleFieldChange('priority', p)} style={{ padding: '6px 14px', borderRadius: 100, border: `1px solid ${card.priority === p ? PRIORITY_COLORS[p] : '#E2E8F0'}`, background: card.priority === p ? PRIORITY_BG[p] : 'white', color: card.priority === p ? PRIORITY_COLORS[p] : '#64748B', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>{p}</button>
                    ))}
                  </div>
                </div>

                {/* Stage */}
                <div>
                  <label>Stage</label>
                  <select value={card.columnId} onChange={(e) => handleFieldChange('columnId', e.target.value)}>
                    {columns.map((col) => <option key={col.id} value={col.id}>{col.name}</option>)}
                  </select>
                </div>

                {/* Client */}
                <div>
                  <label>Client</label>
                  <select value={selectedClientId} onChange={(e) => { setSelectedClientId(e.target.value); handleFieldChange('projectId', null); }}>
                    <option value="">No client</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Project */}
                <div>
                  <label>Project</label>
                  <select value={card.projectId || ''} onChange={(e) => handleFieldChange('projectId', e.target.value || null)}>
                    <option value="">No project</option>
                    {filteredProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <label>Due Date</label>
                  <input type="date" value={card.dueDate ? card.dueDate.split('T')[0] : ''} onChange={(e) => handleFieldChange('dueDate', e.target.value || null)} />
                </div>

                {/* Assignee */}
                <div>
                  <label>Assignee</label>
                  <select value={card.assigneeId || ''} onChange={(e) => handleFieldChange('assigneeId', e.target.value || null)}>
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>

                {/* Delete */}
                {isAdmin && (
                  <div style={{ paddingTop: 4 }}>
                    <button onClick={handleDelete} className="btn btn-danger btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
                      {confirmDelete ? 'Confirm delete?' : '🗑 Delete Card'}
                    </button>
                    {confirmDelete && (
                      <button onClick={() => setConfirmDelete(false)} className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>Cancel</button>
                    )}
                  </div>
                )}
              </div>
            ) : (
            <>
              {/* Left column */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Description */}
                <div>
                  <label>Description</label>
                  <textarea
                    value={card.description || ''}
                    onChange={(e) => setCard((prev) => ({ ...prev, description: e.target.value }))}
                    onBlur={(e) => handleFieldChange('description', e.target.value || null)}
                    placeholder={isAdmin ? 'Add a description...' : 'No description'}
                    rows={4}
                    readOnly={!isAdmin}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                {/* Subtasks */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <label style={{ marginBottom: 0 }}>
                      Sub-tasks {totalSubs > 0 && `(${completedSubs}/${totalSubs})`}
                    </label>
                  </div>

                  {totalSubs > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div
                        style={{
                          height: 4,
                          background: '#E2E8F0',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${progressPct}%`,
                            background: progressPct === 100 ? '#16A34A' : '#2563EB',
                            borderRadius: 2,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3 }}>{progressPct}% complete</div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {card.subTasks?.map((sub) => (
                      <div
                        key={sub.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          background: sub.done ? '#F0FDF4' : '#F8FAFC',
                          borderRadius: 8,
                          border: `1px solid ${sub.done ? '#BBF7D0' : '#E2E8F0'}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={sub.done}
                          onChange={() => handleToggleSubtask(sub)}
                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16A34A' }}
                        />
                        <span
                          style={{
                            flex: 1,
                            fontSize: 13,
                            color: sub.done ? '#64748B' : '#1E293B',
                            textDecoration: sub.done ? 'line-through' : 'none',
                          }}
                        >
                          {sub.label}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteSubtask(sub.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94A3B8',
                              fontSize: 16,
                              padding: '0 2px',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {isAdmin && (
                  <form onSubmit={handleAddSubtask} style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input
                      value={newSubtask}
                      onChange={(e) => setNewSubtask(e.target.value)}
                      placeholder="Add a sub-task..."
                      style={{ flex: 1 }}
                    />
                    <button type="submit" className="btn btn-secondary btn-sm" style={{ flexShrink: 0 }}>
                      Add
                    </button>
                  </form>
                  )}
                </div>
              </div>

              {/* Right sidebar */}
              <div style={{ width: 220, display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0 }}>
                {/* Priority */}
                <div>
                  <label>Priority</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                    {PRIORITY_OPTIONS.map((p) => (
                      <button
                        key={p}
                        onClick={() => handleFieldChange('priority', p)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 100,
                          border: `1px solid ${card.priority === p ? PRIORITY_COLORS[p] : '#E2E8F0'}`,
                          background: card.priority === p ? PRIORITY_BG[p] : 'white',
                          color: card.priority === p ? PRIORITY_COLORS[p] : '#64748B',
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: 'pointer',
                          textTransform: 'capitalize',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stage */}
                <div>
                  <label>Stage</label>
                  <select
                    value={card.columnId}
                    onChange={(e) => handleFieldChange('columnId', e.target.value)}
                    disabled={!isAdmin}
                  >
                    {columns.map((col) => (
                      <option key={col.id} value={col.id}>{col.name}</option>
                    ))}
                  </select>
                </div>

                {/* Client filter */}
                <div>
                  <label>Client</label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => {
                      setSelectedClientId(e.target.value);
                      handleFieldChange('projectId', null);
                    }}
                    disabled={!isAdmin}
                  >
                    <option value="">No client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Project */}
                <div>
                  <label>Project</label>
                  <select
                    value={card.projectId || ''}
                    onChange={(e) => handleFieldChange('projectId', e.target.value || null)}
                    disabled={!isAdmin}
                  >
                    <option value="">No project</option>
                    {filteredProjects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                {/* Due date */}
                <div>
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={card.dueDate ? card.dueDate.split('T')[0] : ''}
                    onChange={(e) => handleFieldChange('dueDate', e.target.value || null)}
                    disabled={!isAdmin}
                  />
                </div>

                {/* Assignee */}
                <div>
                  <label>Assignee</label>
                  <select
                    value={card.assigneeId || ''}
                    onChange={(e) => handleFieldChange('assigneeId', e.target.value || null)}
                    disabled={!isAdmin}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>

                {/* Delete */}
                {isAdmin && (
                <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                  <button
                    onClick={handleDelete}
                    className="btn btn-danger btn-sm"
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {confirmDelete ? 'Confirm delete?' : '🗑 Delete Card'}
                  </button>
                  {confirmDelete && (
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="btn btn-ghost btn-sm"
                      style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                )}
              </div>
            </>
            )
          ) : (
            /* Activity tab */
            <div style={{ flex: 1 }}>
              {/* Stage log */}
              {card.stageLog && card.stageLog.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Stage History
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...card.stageLog].reverse().map((log: StageLog) => (
                      <div
                        key={log.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          fontSize: 13,
                          color: '#475569',
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#2563EB',
                            flexShrink: 0,
                          }}
                        />
                        <span>
                          {log.fromCol ? `${log.fromCol} → ` : 'Created in '}
                          <strong>{log.toCol}</strong>
                        </span>
                        <span style={{ color: '#94A3B8', marginLeft: 'auto', fontSize: 11, flexShrink: 0 }}>
                          {format(new Date(log.movedAt), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Comments
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                  {(!card.comments || card.comments.length === 0) && (
                    <div style={{ color: '#94A3B8', fontSize: 13 }}>No comments yet.</div>
                  )}
                  {card.comments?.map((comment: Comment) => (
                    <div key={comment.id} style={{ display: 'flex', gap: 10 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {comment.author.slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>{comment.author}</span>
                          <span style={{ fontSize: 11, color: '#94A3B8' }}>
                            {format(new Date(comment.createdAt), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        <div
                          style={{
                            background: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            borderRadius: 10,
                            padding: '10px 12px',
                            fontSize: 13,
                            color: '#1E293B',
                            lineHeight: 1.5,
                          }}
                        >
                          {comment.body}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 8 }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={2}
                    style={{ flex: 1, resize: 'none' }}
                  />
                  <button type="submit" className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-end', flexShrink: 0 }}>
                    Post
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
